import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import {
  Accordion,
  ItemGrid,
} from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { UpdateFilterFunction, useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetInfiniteInvoiceCustomersInfiniteQuery } from "store/apis/invoices";
import { useGetInfiniteGroupByProductsServicesInfiniteQuery } from "store/apis/productAndServices";
import { ITEM_TYPES } from "constants/productAndServices";
import {
  CustomerDetailsResponse,
  InvoiceDetailsResponse,
  useGetRevenueScheduleCustomerDetailsQuery,
  useGetRevenueScheduleInvoiceDetailsQuery,
} from "store/apis/revenueSchedule";
import { InvoiceCustomer } from "types/Models/invoice";
import { ArrayElement } from "types/utils/ArrayElement";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import { debounce } from "utils/debouncing";
import { RecognitionStatus } from "constants/deferredRecognition";

const RevenueScheduleContext = createContext<{
  header: string;
  invoiceStartDate?: string;
  invoiceEndDate?: string;
  recognitionStartDate?: string;
  recognitionEndDate?: string;
  recognitionType?: RecognitionStatus;
}>({
  header: "",
  invoiceStartDate: undefined,
  invoiceEndDate: undefined,
  recognitionStartDate: undefined,
  recognitionEndDate: undefined,
  recognitionType: undefined,
});

type PossibleFilters = {
  customers: string[];
  products: string[];
};

type FilterProps = {
  onFilterChange: UpdateFilterFunction<PossibleFilters>;
  filters: PossibleFilters;
};

const Filters = ({ filters, onFilterChange }: FilterProps) => {
  const groupId = useCurrentGroupContext().uuid;
  const entityId = useCurrentEntityId();
  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const [productSearchValue, setProductSearchValue] = useState("");

  const {
    data: customersData,
    isLoading: isCustomerLoading,
    isSuccess: isCustomerLoaded,
    fetchNextPage: fetchNextCustomersPage,
    hasNextPage: hasNextCustomersPage,
  } = useGetInfiniteInvoiceCustomersInfiniteQuery(
    {
      entityId,
      groupId: groupId!,
      search_term: customerSearchValue,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const customers = useMemo(() => {
    return (
      customersData?.pages?.reduce((acc, page) => {
        return [...acc, ...(page.recipients || [])];
      }, [] as InvoiceCustomer[]) || []
    );
  }, [customersData]);

  const onCustomerSearch = useMemo(
    () => debounce((value: string) => setCustomerSearchValue(value)),
    []
  );

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isSuccess: isProductsLoaded,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
  } = useGetInfiniteGroupByProductsServicesInfiniteQuery(
    {
      groupId: groupId!,
      entityId,
      search_term: productSearchValue,
      type: ITEM_TYPES.PRODUCT,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const products = useMemo(
    () => productsData?.pages.flatMap((page) => page.products) || [],
    [productsData]
  );

  const onProductSearch = useMemo(
    () => debounce((value: string) => setProductSearchValue(value)),
    []
  );

  return (
    <Filter.Root
      defaultValue="CUSTOMERS"
      capsule={
        Object.values(filters).some((f) => f.length > 0) && (
          <>
            {filters.customers.map((c) => (
              <Chip
                isActive
                filterType="CUSTOMERS"
                onRemove={() =>
                  onFilterChange(
                    "customers",
                    filters.customers.filter((cust) => cust !== c)
                  )
                }
                key={c}
              >
                {customers?.find((cust) => cust.uuid === c)?.company_name}
              </Chip>
            ))}
            {filters.products.map((c) => (
              <Chip
                isActive
                filterType="PRODUCTS"
                onRemove={() =>
                  onFilterChange(
                    "products",
                    filters.products.filter((prod) => prod !== c)
                  )
                }
                key={c}
              >
                {products.find((prod) => prod.uuid === c)?.name}
              </Chip>
            ))}
          </>
        )
      }
    >
      <Filter.Portal size="small">
        <Filter.List size="small">
          <Filter.ListItem value="CUSTOMERS">Customers</Filter.ListItem>
          <Filter.ListItem value="PRODUCTS">Products</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="CUSTOMERS" block>
          <Async.Root
            isEmpty={customers?.length === 0}
            isLoading={isCustomerLoading}
            isSuccess={isCustomerLoaded}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              {customers && (
                <MultiSelectFilter
                  selected={filters.customers}
                  onChange={(newValues) =>
                    onFilterChange("customers", newValues)
                  }
                  options={customers.map((c) => ({
                    label: c.company_name,
                    value: c.uuid!,
                  }))}
                  onSearchValueChange={onCustomerSearch}
                  hasMore={hasNextCustomersPage}
                  loadNext={fetchNextCustomersPage}
                  totalCount={customers.length}
                />
              )}
            </Async.Success>
          </Async.Root>
        </Filter.Body>
        <Filter.Body value="PRODUCTS" block>
          <Async.Root
            isEmpty={products.length === 0 && !productSearchValue}
            isLoading={isProductsLoading}
            isSuccess={isProductsLoaded}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              <MultiSelectFilter
                selected={filters.products}
                onChange={(newValues) => onFilterChange("products", newValues)}
                options={products.map((c) => ({
                  label: c.name,
                  value: c.uuid!,
                }))}
                onSearchValueChange={onProductSearch}
                hasMore={hasNextProductsPage}
                loadNext={fetchNextProductsPage}
                totalCount={products.length}
              />
            </Async.Success>
          </Async.Root>
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};

const CustomerDetailsTable = ({
  onCustomerSelect,
  values,
  updateFilter,
}: {
  onCustomerSelect: (customerId: string) => void;
  values: FilterProps["filters"];
  updateFilter: FilterProps["onFilterChange"];
}) => {
  const {
    invoiceEndDate,
    invoiceStartDate,
    recognitionType,
    recognitionEndDate,
    recognitionStartDate,
  } = useContext(RevenueScheduleContext);

  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess } =
    useGetRevenueScheduleCustomerDetailsQuery(
      {
        entityId: entityId,
        customer_ids: values.customers.join(","),
        product_ids: values.products.join(","),
        invoice_date_end: invoiceEndDate,
        invoice_date_start: invoiceStartDate,
        recognition_type: recognitionType,
        recognition_date_end: recognitionEndDate,
        recognition_date_start: recognitionStartDate,
      },
      {
        skip: !entityId,
      }
    );

  const columnHelper =
    createColumnHelper<ArrayElement<CustomerDetailsResponse>>();

  const columns = [
    columnHelper.accessor("customer.name", {
      header: "Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("total_amount", {
      header: () => <div className="t-text-right">Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Filters filters={values} onFilterChange={updateFilter} />
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={data?.length === 0}
      >
        <Async.Empty>
          <div className="t-text-center t-py-10 t-text-text-30">
            No customers found
          </div>
        </Async.Empty>
        <Async.Success>
          <TableUI
            size="regular"
            sticky={false}
            table={table}
            onRowClick={(row) => onCustomerSelect(row.original.customer.uuid)}
          />
        </Async.Success>
      </Async.Root>
    </div>
  );
};

const InvoiceItemDetails = ({
  header,
  scheduleDetails,
}: {
  header: string;
  scheduleDetails: ArrayElement<InvoiceDetailsResponse>;
}) => {
  const columnHelper =
    createColumnHelper<(typeof scheduleDetails.revenue_schedule)[0]>();

  const columns = [
    columnHelper.accessor("period", {
      header: "Period",
      cell: (info) => info.getValue(),
      size: 25,
    }),

    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognition Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
      size: 40,
    }),

    columnHelper.accessor("status", {
      header: () => <div className="t-text-right">Status</div>,
      cell: (info) => (
        <div className="t-text-right">
          <Tag
            transparent
            tagType={info.getValue() === "RECOGNIZED" ? "green" : "yellow"}
          >
            {capitalizeFirstLetter(info.getValue().toLowerCase())}
          </Tag>
        </div>
      ),
      size: 35,
    }),
  ];

  const table = useReactTable({
    data: scheduleDetails.revenue_schedule,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { link } = useConstructInternalLink();

  const {
    item_name,
    invoice_number,
    booked_amount,
    invoice_id,
    customer: { name: customerName },
  } = scheduleDetails;

  return (
    <>
      <Slider.Header>
        <Slider.Title>
          <div className="t-flex t-flex-col t-gap-1">
            <Breadcrumb.Root>
              <Slider.RawClose asChild>
                <Breadcrumb.Item as="button">{header}</Breadcrumb.Item>
              </Slider.RawClose>
            </Breadcrumb.Root>
            {item_name} ({invoice_number})
          </div>
        </Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body className="t-flex t-flex-col t-gap-4 t-text-text-60">
        <Accordion.Root defaultValue={"item-details"} type="single" collapsible>
          <Accordion.Item value="item-details">
            <Accordion.Trigger disabled>
              <InfoItem label="Amount Booked">
                <AmountSuperScript amount={Number(booked_amount || 0)} />
              </InfoItem>
            </Accordion.Trigger>
            <Accordion.Content>
              <ItemGrid>
                <InfoItem label="Customer">{customerName}</InfoItem>
                <InfoItem label="Category">Income</InfoItem>
                <InfoItem label="Recognition Period">
                  {dayjs(
                    scheduleDetails.revenue_schedule.at(0)?.period_start
                  ).format("MMM 'YY")}{" "}
                  -{" "}
                  {dayjs(
                    scheduleDetails.revenue_schedule.at(-1)?.period_end
                  ).format("MMM 'YY")}
                </InfoItem>
                <InfoItem label="Linked Invoice">
                  <Link
                    to={link(`/books/invoices-and-customers/${invoice_id}`)}
                  >
                    {invoice_number}
                  </Link>
                </InfoItem>
              </ItemGrid>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
        <TableUI table={table} size="regular" />
      </Slider.Body>
    </>
  );
};

const InvoiceDetailsTable = ({
  values,
  updateFilter,
  header,
}: {
  values: FilterProps["filters"];
  updateFilter: FilterProps["onFilterChange"];
  header: string;
}) => {
  const {
    invoiceEndDate,
    invoiceStartDate,
    recognitionType,
    recognitionEndDate,
    recognitionStartDate,
  } = useContext(RevenueScheduleContext);

  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess } =
    useGetRevenueScheduleInvoiceDetailsQuery(
      {
        entityId: entityId,
        customer_ids: values.customers.join(","),
        product_ids: values.products.join(","),
        invoice_date_end: invoiceEndDate,
        invoice_date_start: invoiceStartDate,
        recognition_type: recognitionType,
        recognition_date_end: recognitionEndDate,
        recognition_date_start: recognitionStartDate,
      },
      {
        skip: !entityId,
      }
    );

  const columnHelper =
    createColumnHelper<ArrayElement<InvoiceDetailsResponse>>();

  const [invoiceSelected, setInvoiceSelected] = useState<string | null>(null);

  const columns = [
    columnHelper.accessor("item_name", {
      header: "Name",
      cell: (info) => {
        return (
          <div className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-body-sm">{info.getValue()}</span>
            <span className="t-text-body-sm t-text-text-30">
              {info.row.original.invoice_number}
            </span>
          </div>
        );
      },
      size: 30,
    }),
    columnHelper.accessor("customer.name", {
      header: "Customer",
      cell: (info) => info.getValue(),
      size: 30,
    }),
    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognised Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
      size: 40,
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedInvoiceItem = data?.find(
    (item) => item.item_id === invoiceSelected
  );

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Filters filters={values} onFilterChange={updateFilter} />
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={data?.length === 0}
      >
        <Async.Empty>
          <div className="t-text-center t-py-10 t-text-text-30">
            No invoices found
          </div>
        </Async.Empty>
        <Async.Success>
          <TableUI
            sticky={false}
            table={table}
            onRowClick={(row) => setInvoiceSelected(row.original.item_id)}
          />
        </Async.Success>
      </Async.Root>
      <Slider.Root
        open={Boolean(invoiceSelected)}
        onOpenChange={(open) => !open && setInvoiceSelected(null)}
      >
        <Slider.Content>
          {data && selectedInvoiceItem && (
            <InvoiceItemDetails
              scheduleDetails={selectedInvoiceItem}
              header={header}
            />
          )}
        </Slider.Content>
      </Slider.Root>
    </div>
  );
};

const Content = ({ header }: { header: string }) => {
  const [currentTab, setCurrentTab] = useState<"INVOICE" | "CUSTOMER">(
    "CUSTOMER"
  );
  const { values, updateFilter } = useFilters({
    initialValue: {
      customers: [] as string[],
      products: [] as string[],
    },
  });
  const handleCustomerSelect = (customerId: string) => {
    updateFilter("customers", [customerId]);
    setCurrentTab("INVOICE");
  };

  return (
    <Tab.Root
      value={currentTab}
      onValueChange={(e) => setCurrentTab(e as typeof currentTab)}
      className="t-flex t-flex-col t-h-full"
    >
      <Slider.Header
        bottom={
          <Tab.List className="!t-border-b-0 !t-mb-0">
            <Tab.Trigger value="CUSTOMER">Customers</Tab.Trigger>
            <Tab.Trigger value="INVOICE">Invoices</Tab.Trigger>
          </Tab.List>
        }
      >
        <Slider.Title>{header}</Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body>
        <Tab.Content value="CUSTOMER">
          <CustomerDetailsTable
            values={values}
            updateFilter={updateFilter}
            onCustomerSelect={handleCustomerSelect}
          />
        </Tab.Content>
        <Tab.Content value="INVOICE">
          <InvoiceDetailsTable
            header={header}
            values={values}
            updateFilter={updateFilter}
          />
        </Tab.Content>
      </Slider.Body>
    </Tab.Root>
  );
};

export const RevenueWaterfallDetailsModal = ({
  header,
  children,
  invoiceStartDate,
  invoiceEndDate,
  recognitionStartDate,
  recognitionEndDate,
  recognitionType,
  onOpen,
  id,
}: {
  children: React.ReactNode;
  header: string;
  invoiceStartDate?: string;
  invoiceEndDate?: string;
  recognitionStartDate?: string;
  recognitionEndDate?: string;
  recognitionType?: RecognitionStatus;
  onOpen: (open: boolean, id: string) => void;
  id: string;
}) => {
  const value = useMemo(
    () => ({
      header,
      invoiceStartDate,
      invoiceEndDate,
      recognitionStartDate,
      recognitionEndDate,
      recognitionType,
    }),
    [
      header,
      invoiceStartDate,
      invoiceEndDate,
      recognitionStartDate,
      recognitionEndDate,
      recognitionType,
    ]
  );

  const slider = useModal();

  useEffect(() => {
    slider.isOpen ? onOpen(true, id) : onOpen(false, id);
  }, [slider.isOpen]);

  return (
    <RevenueScheduleContext.Provider value={value}>
      <Slider.Root
        onOpenChange={slider.toggle}
        open={slider.isOpen}
        modal={false}
      >
        <Slider.Trigger asChild>{children}</Slider.Trigger>
        <Slider.Content useCustomOverlay>
          <Content header={header} />
        </Slider.Content>
      </Slider.Root>
    </RevenueScheduleContext.Provider>
  );
};
